import { Component, AfterViewInit, ElementRef, EventEmitter, forwardRef, Input, OnInit, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Observable ,  Subscription  } from 'rxjs';

import { ApiError, AuthService, Task, TaskService, MessageService } from '../../../core';
import { BaseTypeahead, BaseTypeaheadService } from '../../../core/services/typeahead';
import { IntegrationItem, IntegrationItemHttpService } from '../../configuracoes/integration/item';
import { periodicidadeDiaria } from '../../configuracoes/integration';
import { Item } from '../item';
import { TypeaheadItemHttpService } from './typeahead-item-http.service';
import { ItemFilterSearch, ItemFilterModalComponent } from '../filter';
import { isSituacaoInativo } from '../item-situacao';

import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { TypeaheadItemOptions } from './typeahead-item-options';

@Component({
    selector: 'sgt-typeahead-item',
    templateUrl: './typeahead-item.component.html',
    styleUrls: ['./typeahead-item.component.less'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TypeaheadItemComponent), multi: true },
    ]
})
export class TypeaheadItemComponent implements OnInit, AfterViewInit, OnChanges, BaseTypeahead<Item> {
    @Input() entity: Item;
    @Output() ngModelChange = new EventEmitter<Item>();

    @Input() required: any;
    @Input() disabled: any;
    @Input() readonly: any;
    @Input() ngControl: NgControl;
    @Input() autofocus = false;
    @Input() dropup = false;
    @Input() typeaheadOptionsInScrollableView = 5; // Quantidade de itens visíveis no no scroll do typeahead
    @Input() container: string;
    @Output() typeaheadChange = new EventEmitter<Item>();
    // Filters
    filter = new ItemFilterSearch();
    @Input() situacao: number = null;
    @Input() idPessoa: number = null;
    @Input() idItemMarca: number = null;
    @Input() idUnidadeNegocio: number = null;
    @Input() idItemTipo: number = null;
    @Input() idNaturezaVenda: number = null;
    @Input() idMercado: number = null;
    @Input() idLinhaProducao: number = null;
    @Input() exibirDataUltimoFaturamento = false;
    @Input() ignorarRepresentante = false; // Se deve ignorar o representante e seu atendente e gerente no filtro dos itens
    // Options
    options = new TypeaheadItemOptions();
    @Input() canSincronizarErp = false;

    @ViewChild('itemFilterModal') itemFilterModal: ItemFilterModalComponent;

    @ViewChild('inputTypeahead', {static: true}) inputTypeahead: ElementRef;
    typeaheadOptionsLimit = 1000; // Quantidade máxima de registros exibidos no typeahead
    typeaheadSelected: string;
    typeaheadToken: string;
    typeaheadDataSource: Observable<Item[]>;
    typeaheadNoResults: boolean;

    currentRequest: Subscription;
    isLoading: boolean;

    propagateChange: any = () => {};

    constructor(private baseTypeaheadService: BaseTypeaheadService,
                private authService: AuthService,
                private taskService: TaskService,
                private messageService: MessageService,
                private typeaheadItemHttpService: TypeaheadItemHttpService,
                private integrationItemHttpService: IntegrationItemHttpService) { }

    ngOnInit() {
        this.setOptions();
        this.createDataSource();
    }

    ngAfterViewInit() {
        if (this.autofocus) {
            jQuery(this.inputTypeahead.nativeElement).focus();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            // Sincronizar com o ERP
            if (propName === 'canSincronizarErp') {
                if (!changes[propName].isFirstChange()) {
                    this.setOptions();
                }
            }
        }
    }

    /** Seta as opções do typeahead */
    private setOptions() {
        if (!this.options) {
            this.options = new TypeaheadItemOptions();
        }
        // Sincronizar com o ERP
        this.options.canSincronizarErp = this.canSincronizarErp;
    }

    /** Cria o data source do typeahead */
    createDataSource() {
        this.typeaheadDataSource = Observable.create((observer: any) => {
            if (this.currentRequest) {
                this.currentRequest.unsubscribe();
            }

            this.setFilter(this.typeaheadToken);
            this.currentRequest = this.typeaheadItemHttpService.search(this.filter, this.typeaheadOptionsLimit)
                                        .subscribe(
                                            (entities: Item[]) => observer.next(entities),
                                            (error: ApiError) => {
                                                this.messageService.error(error.message, 'Ocorreu um erro ao pesquisar os itens. Tente novamente', error);
                                                observer.next([]);
                                            }
                                        );
        });
    }

    /** Seta os valores padrões e da pesquisa no filter para pesquisar os itens */
    private setFilter(filterPsq: string = null) {
        this.filter.clear();
        this.filter.filterPsq = filterPsq;
        this.filter.situacao = this.situacao;
        this.filter.idPessoa = this.idPessoa;
        this.filter.idItemMarca = this.idItemMarca;
        // Unidade de Negócio
        if (this.idUnidadeNegocio) {
            this.filter.idsUnidadesNegocios = [this.idUnidadeNegocio];
        }
        // Mercado
        if (this.idMercado) {
            this.filter.idsMercados = [this.idMercado];
        }
        // Natureza de Venda
        if (this.idNaturezaVenda) {
            // this.filter.idsNaturezasVendas = [this.idNaturezaVenda]; // TODO: COMENTADO TEMPORARIAMENTE ENQUANTO A RAFITEC/PROPEX FAZ A MIGRAÇÃO DE SUAS NOVAS NATUREZAS DE VENDAS
        }
        // Tipo de Item
        if (this.idItemTipo) {
            this.filter.idsItensTipos = [this.idItemTipo];
        }

        this.filter.idLinhaProducao = this.idLinhaProducao;

        // Se for a Rafitec, nunca ignorar o representante. Se não, considera valor informado. // TODO: Melhorar esta regra para não precisar verificar se é a Rafitec
        this.filter.ignorarRepresentante = this.authService.isRafitec() ? false : this.ignorarRepresentante;
    }

    /** Seta o foco no input */
    setFocus() {
        jQuery(this.inputTypeahead.nativeElement).focus();
    }

    /** Ao digitar no input do typeahead */
    onKeyup(e: KeyboardEvent) {
        this.baseTypeaheadService.onKeyup(this, e);
    }

    /** Ao sair do input do typeahead */
    onBlur() {
        this.baseTypeaheadService.onBlur(this);
    }


    /** Verifica se o typeahead possui resultados */
    onChangeTypeaheadNoResults(noResults: boolean) {
        this.typeaheadNoResults = noResults;
    }

    /** Ao selecionar o item no typeahead */
    onSelectEntity(tm: TypeaheadMatch) {
        this.baseTypeaheadService.onSelectEntity(this, tm);
    }

    /** Ao selecionar o cliente no modal de pesquisa */
    onSelectEntityModal(item: Item) {
        if (item) {
            this.entity = item;
        } else {
            this.entity = null;
        }

        this.propagateChange(this.entity);
        this.typeaheadChange.emit(this.entity);
        this.writeValue(this.entity);
        jQuery(this.inputTypeahead.nativeElement).focus();
    }

    /** Abre o modal de pesquisa */
    search() {
        this.setFilter();
        this.itemFilterModal.open(this.filter);
    }

    /** Limpa o typeahead e o model */
    clear() {
        this.baseTypeaheadService.clear(this);
    }

   /** Quando recebe um objeto item de "fora" do component */
    writeValue(value: Item) {
        if (value) {
            this.baseTypeaheadService.writeValue(this, value, value.nome);
        } else {
            this.baseTypeaheadService.writeValue(this, null);
        }
    }

    /** Propaga as alterações */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched() {}

    /** Verifica se a situação do item é inativo */
    isInativo(situacao: number): boolean {
        return isSituacaoInativo(situacao);
    }

    /** Executa as integrações de leituras dos itens nos sistemas ERP's manualmente */
    sincronizarErp() {
        const task = new Task('Sincronizando itens com o ERP', 'Executando sincronização dos itens com o sistema ERP');
        this.taskService.add(task, task.description);

        const integration = new IntegrationItem();
        integration.periodicidade = periodicidadeDiaria.id; // Sempre executamos a períodicidade diária para sincronizar apenas os itens cadastrados nas últimas 24 horas
        this.integrationItemHttpService.execReadIntegrationManual(integration)
            .subscribe(
                () => this.taskService.success(task, 'Sincronização dos itens com o sistema ERP realizada com sucesso'),
                (error: ApiError) => this.taskService.error(task, 'Erro ao realizar a sincronização dos itens com o sistema ERP', error.message, error)
            );
    }
}
